/**
 * Phase 18.16-B Batch 2 — Tool-page banner + CTA band shared components.
 *
 * The page-banner component is a thin wrapper around <PageBanner> that
 * pulls eyebrow/title/highlight/subtitle + a USP-badge ribbon and the
 * optional banner CTA buttons from the CMS payload (see
 * `useToolPageCms`). Static props (image, image alt, eyebrow icon,
 * data-testid, extra children like ShippingInfoButton) stay code-driven
 * to keep the existing tool UX intact.
 *
 * The CTA band component renders below the tool itself. Hidden if the
 * CMS title is empty so admins can completely opt out of the band.
 *
 * Both components NEVER touch the actual tool functionality.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Package, Truck, ShieldCheck, Zap, Receipt, Banknote,
  Headphones, CheckCircle2, Clock, Sparkles, Info, Briefcase, Ship,
  Plane, MapPin, User, Car,
} from "lucide-react";
import PageBanner from "./PageBanner";

const BADGE_ICONS = {
  ship: Ship, plane: Plane, package: Package, truck: Truck, car: Car,
  map: MapPin, briefcase: Briefcase, user: User,
  check: CheckCircle2, clock: Clock, shield: ShieldCheck,
  banknote: Banknote, support: Headphones, sparkles: Sparkles,
  info: Info, receipt: Receipt, zap: Zap,
};
const resolveBadgeIcon = (k) => BADGE_ICONS[String(k || "").toLowerCase()] || ShieldCheck;

/**
 * @param {object} props
 * @param {object} props.banner   — CMS page_banner sub-doc (or fallback)
 * @param {string} [props.image]
 * @param {string} [props.imageAlt]
 * @param {React.ComponentType} [props.eyebrowIcon]
 * @param {string} [props.testid]
 * @param {React.ReactNode} [props.extras]  — rendered after the badges
 *        (e.g. ShippingInfoButton on Paklijst/Boekingsformulier).
 */
export function ToolPageBanner({
  banner = {},
  image,
  imageAlt,
  eyebrowIcon,
  testid,
  extras,
}) {
  const badges = Array.isArray(banner.badges) ? banner.badges : [];
  return (
    <PageBanner
      image={image}
      imageAlt={imageAlt || banner.title || ""}
      eyebrow={banner.eyebrow}
      eyebrowIcon={eyebrowIcon}
      title={banner.title}
      highlight={banner.highlight}
      description={banner.subtitle}
      testid={testid}
    >
      {/*
        Phase 18.16-B fix Feb 2026 — wrap the three child groups (badges,
        banner CTA-row, page-specific extras) in a single space-y stack
        so the login / shipping-info buttons no longer collide with the
        USP-badges row on mobile (was gap=0px on /my5999cargo @390).
      */}
      <div
        className="space-y-5 sm:space-y-6"
        data-testid={testid ? `${testid}-stack` : "tool-banner-stack"}
      >
        {badges.length > 0 ? (
          <ul
            className="flex flex-wrap gap-2"
            data-testid={testid ? `${testid}-badges` : "tool-banner-badges"}
          >
            {badges.map((b, i) => {
              const Icon = resolveBadgeIcon(b.icon);
              return (
                <li
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[12px] tracking-wide font-semibold text-white"
                >
                  <Icon className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.25} />
                  {b.label}
                </li>
              );
            })}
          </ul>
        ) : null}
        {(banner.cta_primary_label && banner.cta_primary_url) ||
         (banner.cta_secondary_label && banner.cta_secondary_url) ? (
          <div
            className="flex flex-col sm:flex-row gap-3"
            data-testid={testid ? `${testid}-ctas` : "tool-banner-ctas"}
          >
            {banner.cta_primary_label && banner.cta_primary_url ? (
              <CtaLink
                href={banner.cta_primary_url}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-sm hover:bg-cyan-50 transition-all"
                testid={testid ? `${testid}-cta-primary` : undefined}
              >
                {banner.cta_primary_label}
                <ArrowRight className="w-4 h-4" />
              </CtaLink>
            ) : null}
            {banner.cta_secondary_label && banner.cta_secondary_url ? (
              <CtaLink
                href={banner.cta_secondary_url}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white/10 border border-white/30 text-white hover:bg-white/15 font-semibold tracking-tight text-sm transition-all backdrop-blur-sm"
                testid={testid ? `${testid}-cta-secondary` : undefined}
              >
                {banner.cta_secondary_label}
              </CtaLink>
            ) : null}
          </div>
        ) : null}
        {extras}
      </div>
    </PageBanner>
  );
}

/**
 * Optional CTA band rendered at the bottom of a tool page. Hidden when
 * the CMS title is empty.
 */
export function ToolPageCtaBand({ cta = {}, testid = "tool-cta-band" }) {
  if (!cta || !cta.title) return null;
  return (
    <section
      className="relative overflow-hidden bg-[#082c50] text-white py-14 sm:py-16"
      data-testid={testid}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute right-0 bottom-0 w-[28rem] h-[28rem] rounded-full bg-[#1a5193]/30 blur-3xl" />
      </div>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.3fr,0.7fr] gap-8 items-center">
        <div>
          <h3 className="font-chivo font-bold text-2xl sm:text-3xl tracking-tight leading-tight">
            {cta.title}
          </h3>
          {cta.description ? (
            <p className="mt-3 text-white/75 max-w-xl leading-relaxed">{cta.description}</p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
          {cta.cta_primary_label && cta.cta_primary_url ? (
            <CtaLink
              href={cta.cta_primary_url}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-sm hover:bg-cyan-50 transition-all"
              testid={`${testid}-primary`}
            >
              {cta.cta_primary_label}
              <ArrowRight className="w-4 h-4" />
            </CtaLink>
          ) : null}
          {cta.cta_secondary_label && cta.cta_secondary_url ? (
            <CtaLink
              href={cta.cta_secondary_url}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white/0 border border-white/30 text-white hover:bg-white/10 font-semibold tracking-tight text-sm transition-all"
              testid={`${testid}-secondary`}
            >
              {cta.cta_secondary_label}
            </CtaLink>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function CtaLink({ href, className, testid, children }) {
  const isExternal = /^(https?:|tel:|mailto:|#)/.test(href || "");
  if (isExternal) {
    return <a href={href} className={className} data-testid={testid}>{children}</a>;
  }
  return <Link to={href || "#"} className={className} data-testid={testid}>{children}</Link>;
}
